import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import { execBookingAction, useBookingByIdWithMetaQueryObserver, useBookingNotesQuery } from "../../../../api/services";
import DialogCloseButton from "../../../../components/DialogCloseButton";
import { FormTextField } from "../../../../components/fields";
import useDialog from "../../../../hooks/useDialog";
import { BookingAction, RouteParams } from "../../../../types";

interface IFormValues {
    note: string
}

const BlacklistCheckAction = () => {

    const { t } = useTranslation('booking');

    const { dispatchGlobalData } = useContext(GlobalContext);

    const { id } = useParams<RouteParams>();

    const dialog = useDialog();

    const form = useForm<IFormValues>({
        defaultValues: {
            note: ''
        },
        reValidateMode: 'onSubmit',
        shouldUnregister: true
    });

    const bookingQuery = useBookingByIdWithMetaQueryObserver(id);
    const bookingNotesQuery = useBookingNotesQuery(id);

    const execBookingActionMutation = useMutation(
        execBookingAction,
        {
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            },
            onSuccess: (data) => {
                bookingQuery.refetch();
                bookingNotesQuery.refetch();
            },
            onSettled: () => {
                dialog.handleClose()
            }
        }
    );

    const onClickApprove = useCallback(() => {
        if (!bookingQuery.data) {
            return;
        }
        execBookingActionMutation.mutate({
            bookingId: bookingQuery.data.booking.id,
            action: BookingAction.Blacklist,
            subaction: 'APPROVE',
            note: form.getValues('note') === '' ? undefined : form.getValues('note'),
            lastUpdateTime: bookingQuery.data.booking.requestUpdateDate
        });
    }, [bookingQuery.data]);
    const onClickReject = useCallback(() => {
        if (!bookingQuery.data) {
            return;
        }
        execBookingActionMutation.mutate({
            bookingId: bookingQuery.data.booking.id,
            action: BookingAction.Blacklist,
            subaction: 'REJECTED',
            note: form.getValues('note') || undefined,
            lastUpdateTime: bookingQuery.data.booking.requestUpdateDate
        });
    }, [bookingQuery.data]);

    return (
        <>
            {bookingQuery.data?.permissions.actions[BookingAction.Blacklist] &&
                <>
                    <Button onClick={dialog.handleShow}>
                        {t("label.watchlistCheck")}
                    </Button>
                    <Dialog
                        open={dialog.open}
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogCloseButton onClick={dialog.handleClose} />
                        <DialogTitle>
                            {t('label.watchlistCheck')}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {t("message.info.blacklist_booking_action")}
                            </DialogContentText>
                            <FormTextField<IFormValues>
                                name="note"
                                control={form.control}
                                label="Note"
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="outlined"
                                onClick={dialog.handleClose}
                            >
                                {t('label.back')}
                            </Button>
                            <Button onClick={onClickApprove}>
                                {t('label.approve')}
                            </Button>
                            <Button onClick={form.handleSubmit(onClickReject)}>
                                {t('label.reject')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            }
        </>
    );
};

export default BlacklistCheckAction;
